import { useId, useState } from "react";

import { EFFORT_OPTS, MODEL_OPTS } from "@tinsmith/model-options";

import styles from "./Routines.module.css";
import {
  DEFAULT_SCHEDULE,
  fromCron,
  type ScheduleDraft,
  type ScheduleKind,
  toCron,
  WEEKDAYS,
} from "./schedule";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Segmented } from "../../components/Segmented";
import { Select } from "../../components/Select";
import { useRepoBranches } from "../../data/api/repositories";
import {
  type ApiRoutine,
  createRoutine,
  OVERLAP_HELP,
  OVERLAP_LABEL,
  ROUTINE_OVERLAPS,
  type RoutineCreate,
  type RoutineOverlap,
  type RoutinePatch,
  updateRoutine,
} from "../../data/api/routines";
import { Icon } from "../../icons";
import { cx } from "../../lib/cx";
import { SettingsRow, useAction } from "../settings/parts";
import settings from "../settings/Settings.module.css";

// The Select's value for "unset". A colon can't appear in a git ref, so it
// can't collide with a branch name.
const REPO_DEFAULT = ":default";

const choiceOf = (value: string | null): string => value ?? REPO_DEFAULT;
const valueOf = (choice: string): string | null =>
  choice === REPO_DEFAULT ? null : choice;

const withDefault = (
  values: ReadonlyArray<string>,
): ReadonlyArray<{ readonly label: string; readonly value: string }> => [
  { label: "Repository default", value: REPO_DEFAULT },
  ...values.map((value) => ({ label: value, value })),
];

const MODEL_CHOICES = withDefault(MODEL_OPTS.map((o) => o.value));
const EFFORT_CHOICES = withDefault(EFFORT_OPTS.map((o) => o.value));

const KIND_OPTS: ReadonlyArray<{
  readonly label: string;
  readonly value: ScheduleKind;
}> = [
  { label: "Hourly", value: "hourly" },
  { label: "Daily", value: "daily" },
  { label: "Weekdays", value: "weekdays" },
  { label: "Weekly", value: "weekly" },
  { label: "Custom", value: "custom" },
];

const WEEKDAY_OPTS = WEEKDAYS.map((label, day) => ({
  label,
  value: String(day),
}));

// V8 leaves UTC out of the list.
const LISTED_ZONES = Intl.supportedValuesOf("timeZone");
const TIME_ZONES = LISTED_ZONES.includes("UTC")
  ? LISTED_ZONES
  : ["UTC", ...LISTED_ZONES];

// Built once: some 400 options would otherwise be reconciled on every
// keystroke anywhere in the form.
const ZONE_OPTIONS = TIME_ZONES.map((zone) => (
  <option key={zone} value={zone} />
));

const pad = (n: number): string => String(n).padStart(2, "0");

const FIELD_KEYS = [
  "base_branch",
  "effort",
  "model",
  "name",
  "overlap",
  "prompt",
  "schedule",
  "timezone",
] as const;

type Fields = Pick<ApiRoutine, (typeof FIELD_KEYS)[number]>;

// Only what moved, so the audit names just the fields that changed.
const changesFrom = (routine: ApiRoutine, fields: Fields): RoutinePatch =>
  Object.fromEntries(
    FIELD_KEYS.filter((key) => fields[key] !== routine[key]).map((key) => [
      key,
      fields[key],
    ]),
  );

const createBody = ({
  base_branch,
  effort,
  model,
  ...rest
}: Fields): RoutineCreate => ({
  ...rest,
  ...(base_branch === null ? {} : { base_branch }),
  ...(effort === null ? {} : { effort }),
  ...(model === null ? {} : { model }),
});

type Draft = {
  readonly base: string;
  readonly effort: string;
  readonly model: string;
  readonly name: string;
  readonly overlap: RoutineOverlap;
  readonly prompt: string;
  readonly schedule: ScheduleDraft;
  readonly timezone: string;
};

const toDraft = (routine: ApiRoutine | undefined): Draft =>
  routine
    ? {
        base: choiceOf(routine.base_branch),
        effort: choiceOf(routine.effort),
        model: choiceOf(routine.model),
        name: routine.name,
        overlap: routine.overlap,
        prompt: routine.prompt,
        schedule: fromCron(routine.schedule),
        timezone: routine.timezone,
      }
    : {
        base: REPO_DEFAULT,
        effort: REPO_DEFAULT,
        model: REPO_DEFAULT,
        name: "",
        overlap: ROUTINE_OVERLAPS[0],
        prompt: "",
        schedule: DEFAULT_SCHEDULE,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

type ScheduleFieldProps = {
  readonly draft: ScheduleDraft;
  readonly onChange: (draft: ScheduleDraft) => void;
};

const ScheduleField = ({ draft, onChange }: ScheduleFieldProps) => {
  const set = (patch: Partial<ScheduleDraft>): void =>
    onChange({ ...draft, ...patch });
  // Custom opens on the expression the preset was writing, to edit from.
  const setKind = (kind: ScheduleKind): void =>
    set(kind === "custom" ? { custom: toCron(draft), kind } : { kind });

  return (
    <>
      <Segmented
        aria-label="Repeats"
        onValueChange={(v) => setKind(v as ScheduleKind)}
        options={KIND_OPTS}
        value={draft.kind}
      />
      <div className={styles.inline}>
        {draft.kind === "custom" ? (
          <Input
            aria-label="Cron expression"
            // `mono` and not a font of its own: an input is out of reach of
            // the global `code` rule, and ligatures would merge its glyphs.
            className={cx(styles.cron, "mono")}
            onChange={(e) => set({ custom: e.target.value })}
            placeholder="0 9 * * 1-5"
            value={draft.custom}
          />
        ) : draft.kind === "hourly" ? (
          <>
            At minute
            <Input
              aria-label="Minute past the hour"
              className={styles.minute}
              max={59}
              min={0}
              onChange={(e) => {
                const minute = Number(e.target.value);
                if (Number.isInteger(minute) && minute >= 0 && minute <= 59) {
                  set({ minute });
                }
              }}
              type="number"
              value={draft.minute}
            />
          </>
        ) : (
          <>
            {draft.kind === "weekly" ? (
              <>
                On
                <Select
                  onValueChange={(v) => set({ weekday: Number(v) })}
                  options={WEEKDAY_OPTS}
                  value={String(draft.weekday)}
                />
              </>
            ) : null}
            at
            <Input
              aria-label="Time"
              className={styles.time}
              onChange={(e) => {
                const [hour, minute] = e.target.value.split(":").map(Number);
                if (hour !== undefined && minute !== undefined) {
                  set({ hour, minute });
                }
              }}
              type="time"
              value={`${pad(draft.hour)}:${pad(draft.minute)}`}
            />
          </>
        )}
      </div>
      <div className={settings.help}>
        {draft.kind === "custom" ? (
          "Five-field cron: minute, hour, day of month, month, day of week. At most once an hour."
        ) : (
          <>
            Cron <code>{toCron(draft)}</code>
          </>
        )}
      </div>
    </>
  );
};

export type RoutineFormProps = {
  /** The routine being edited; absent for a new one. */
  readonly initial?: ApiRoutine;
  readonly onCancel: () => void;
  readonly onSaved: (routine: ApiRoutine) => void;
  readonly repoId: string;
};

export const RoutineForm = ({
  initial,
  onCancel,
  onSaved,
  repoId,
}: RoutineFormProps) => {
  const id = useId();
  const [draft, setDraft] = useState(() => toDraft(initial));
  const [advanced, setAdvanced] = useState(
    Boolean(initial?.base_branch ?? initial?.model ?? initial?.effort),
  );
  const { busy, error, run } = useAction();
  const { branches } = useRepoBranches(advanced ? repoId : null);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]): void =>
    setDraft((d) => ({ ...d, [key]: value }));

  const fields: Fields = {
    base_branch: valueOf(draft.base),
    effort: valueOf(draft.effort),
    model: valueOf(draft.model),
    name: draft.name.trim(),
    overlap: draft.overlap,
    prompt: draft.prompt.trim(),
    schedule: toCron(draft.schedule),
    timezone: draft.timezone.trim(),
  };
  const ready =
    fields.name.length > 0 &&
    fields.prompt.length >= 3 &&
    fields.schedule.length > 0 &&
    fields.timezone.length > 0;

  const branchChoices = withDefault([
    ...new Set([
      ...branches.map((b) => b.name),
      ...(fields.base_branch ? [fields.base_branch] : []),
    ]),
  ]);

  const save = (): void => {
    if (!ready || busy) {
      return;
    }
    const patch = initial ? changesFrom(initial, fields) : null;
    if (initial && patch && Object.keys(patch).length === 0) {
      onCancel();
      return;
    }
    run(
      () =>
        (initial && patch
          ? updateRoutine(initial.public_id, patch)
          : createRoutine(repoId, createBody(fields))
        ).then(onSaved),
      "Couldn't save the routine.",
    );
  };

  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
    >
      <div className={styles.field}>
        <label className={settings.label} htmlFor={`${id}-name`}>
          Name
        </label>
        <Input
          id={`${id}-name`}
          maxLength={100}
          onChange={(e) => set("name", e.target.value)}
          placeholder="Weekly dependency bumps"
          value={draft.name}
        />
      </div>

      <div className={styles.field}>
        <label className={settings.label} htmlFor={`${id}-prompt`}>
          Prompt
        </label>
        <div className={settings.help}>
          Each run starts from a fresh clone and remembers nothing, so say
          everything the agent needs.
        </div>
        <textarea
          className={styles.promptInput}
          id={`${id}-prompt`}
          maxLength={20_000}
          onChange={(e) => set("prompt", e.target.value)}
          placeholder="Bump every dependency, run the tests and open a pull request."
          rows={5}
          value={draft.prompt}
        />
      </div>

      <div className={styles.field}>
        <span className={settings.label}>Schedule</span>
        <ScheduleField
          draft={draft.schedule}
          onChange={(schedule) => set("schedule", schedule)}
        />
      </div>

      <div className={styles.field}>
        <label className={settings.label} htmlFor={`${id}-zone`}>
          Timezone
        </label>
        <Input
          id={`${id}-zone`}
          list={`${id}-zones`}
          onChange={(e) => set("timezone", e.target.value)}
          value={draft.timezone}
        />
        <datalist id={`${id}-zones`}>{ZONE_OPTIONS}</datalist>
      </div>

      <fieldset className={styles.field}>
        <legend className={settings.label}>
          If the last run is still open
        </legend>
        {ROUTINE_OVERLAPS.map((overlap) => (
          <label className={styles.choice} key={overlap}>
            <input
              checked={draft.overlap === overlap}
              name={`${id}-overlap`}
              onChange={() => set("overlap", overlap)}
              type="radio"
              value={overlap}
            />
            <span className={styles.choiceText}>
              <span>{OVERLAP_LABEL[overlap]}</span>
              <span className={settings.help}>{OVERLAP_HELP[overlap]}</span>
            </span>
          </label>
        ))}
      </fieldset>

      <div>
        <Button
          aria-expanded={advanced}
          onClick={() => setAdvanced((a) => !a)}
          size="sm"
          variant="ghost"
        >
          <Icon.Chevron
            className={cx(styles.chevron, advanced && styles.chevronOpen)}
            size={12}
          />
          Advanced
        </Button>
        {advanced ? (
          <div className={cx(settings.card, styles.advanced)}>
            <SettingsRow help="Resolved when each run starts." label="Model">
              <Select
                onValueChange={(v) => set("model", v)}
                options={MODEL_CHOICES}
                value={draft.model}
              />
            </SettingsRow>
            <SettingsRow label="Effort">
              <Select
                onValueChange={(v) => set("effort", v)}
                options={EFFORT_CHOICES}
                value={draft.effort}
              />
            </SettingsRow>
            <SettingsRow label="Base branch">
              <Select
                onValueChange={(v) => set("base", v)}
                options={branchChoices}
                value={draft.base}
              />
            </SettingsRow>
          </div>
        ) : null}
      </div>

      {error ? (
        <div className={styles.failure} role="alert">
          {error}
        </div>
      ) : null}

      <div className={styles.formActions}>
        <Button onClick={onCancel} size="sm" variant="ghost">
          Cancel
        </Button>
        <Button
          disabled={!ready || busy}
          size="sm"
          type="submit"
          variant="accent"
        >
          {busy ? "Saving…" : initial ? "Save changes" : "Create routine"}
        </Button>
      </div>
    </form>
  );
};
